
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useCallback, useEffect, useRef, useState } from 'react';
import ContentPasteSearchOutlinedIcon from '@mui/icons-material/ContentPasteSearchOutlined';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { Avatar, Box, Card, Chip, Divider, IconButton, InputBase, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import Loading from '../load/loading';
import MessageError from '../messages/messageError';
import StatusEstabelecimento from './status/statusEstabelecimento';

const DEBOUNCE_MS = 400;

const useStales = () => ({
    icone: {
        margin: 7,
        cursor: 'pointer',
        '&:hover': { backgroundColor: '#b1b7ea', borderRadius: 3 },
    },
    hover: { '&:hover': { backgroundColor: '#d2d6ff', cursor: 'pointer' } },
});

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': { padding: theme.spacing(2) },
}));

export default function PesquisarEstabelecimento(props) {
    const navigate = useNavigate();
    const classes = useStales();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [pesquisa, setPesquisa] = useState(null);
    const [openLoad, setOpenLoad] = useState(false);
    const [messageError, setMessageError] = useState('');
    const debounceRef = useRef(null);

    const handleClickOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setSearch('');
        setPesquisa(null);
        setMessageError('');
    };

    const fetchEstabelecimentos = useCallback(async (termo) => {
        if (!termo.trim()) {
            setPesquisa(null);
            return;
        }
        setPesquisa(null);
        setMessageError('');
        setOpenLoad(true);
        try {
            const response = await api.post(`/estabelecimento/search?perPage=20`, { search: termo });
            setPesquisa(response.data.estabelecimentos);
        } catch (error) {
            setMessageError(
                error.message === 'Network Error'
                    ? 'Erro de conexão com o servidor.'
                    : error.response?.data?.message ?? 'Erro ao pesquisar.'
            );
            setPesquisa([]);
        } finally {
            setOpenLoad(false);
        }
    }, []);

    // debounce: dispara a busca automaticamente ao digitar
    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            fetchEstabelecimentos(search);
        }, DEBOUNCE_MS);
        return () => clearTimeout(debounceRef.current);
    }, [search, fetchEstabelecimentos]);

    const handleClear = () => {
        setSearch('');
        setPesquisa(null);
        setMessageError('');
    };

    return (
        <div>
            <ContentPasteSearchOutlinedIcon
                onClick={handleClickOpen}
                titleAccess="Pesquisar Estabelecimento"
                style={classes.icone}
            />

            <BootstrapDialog
                fullWidth
                maxWidth="md"
                onClose={handleClose}
                aria-labelledby="search-dialog-title"
                open={open}
            >
                <DialogTitle id="search-dialog-title" sx={{ pb: 0 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="subtitle1" fontWeight="bold">
                            Pesquisar Estabelecimento
                        </Typography>
                        <IconButton size="small" onClick={handleClose}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    {messageError && <MessageError message={messageError} />}

                    {/* Barra de busca */}
                    <Paper
                        variant="outlined"
                        sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.5, mb: 2, borderRadius: 2 }}
                    >
                        <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                        <InputBase
                            fullWidth
                            autoFocus
                            // placeholder="Digite o nome, NIF ou número de processo…"
                            placeholder="Pesquisar estabelecimento"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            inputProps={{ 'aria-label': 'pesquisar estabelecimento' }}
                        />
                        {search && (
                            <IconButton size="small" onClick={handleClear} aria-label="limpar pesquisa">
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Paper>

                    {openLoad && <Loading text="A pesquisar…" />}

                    {/* Resultados */}
                    {Array.isArray(pesquisa) && !openLoad && (
                        <>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <Chip
                                    size="small"
                                    label={pesquisa.length === 0
                                        ? 'Nenhum resultado'
                                        : `${pesquisa.length} resultado${pesquisa.length !== 1 ? 's' : ''}`}
                                    color={pesquisa.length === 0 ? 'default' : 'primary'}
                                    variant="outlined"
                                />
                                {pesquisa.length === 20 && (
                                    <Typography variant="caption" color="text.secondary">
                                        Mostrando os primeiros 20 - refine a pesquisa para mais precisão.
                                    </Typography>
                                )}
                            </Box>
                            <Divider sx={{ mb: 1 }} />

                            {pesquisa.length === 0 ? (
                                <Box textAlign="center" py={4}>
                                    <ContentPasteSearchOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                                    <Typography variant="body2" color="text.secondary" mt={1}>
                                        Nenhum estabelecimento encontrado para "<strong>{search}</strong>"
                                    </Typography>
                                </Box>
                            ) : (
                                <Card variant="outlined">
                                    <TableContainer sx={{ maxHeight: 400 }}>
                                        <Table id="myTable" size="small" stickyHeader aria-label="resultados da pesquisa">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell><Typography noWrap><strong>Imagem</strong></Typography></TableCell>
                                                    <TableCell><Typography noWrap><strong>Nome</strong></Typography></TableCell>
                                                    <TableCell><Typography noWrap><strong>N.º Processo</strong></Typography></TableCell>
                                                    <TableCell><Typography noWrap><strong>Director Técnico</strong></Typography></TableCell>
                                                    <TableCell><Typography noWrap><strong>Estado</strong></Typography></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {pesquisa.map((est) => (
                                                    <TableRow
                                                        key={est?._id}
                                                        sx={classes.hover}
                                                        onClick={() => { navigate(`/admin/gestor-licenciados/${est?._id}`); handleClose(); }}
                                                        title={est?.nome}
                                                    >
                                                        <TableCell>
                                                            <Avatar variant="square" src={est?.logo} alt={est?.nome} />
                                                        </TableCell>
                                                        <TableCell>{est?.nome}</TableCell>
                                                        <TableCell>{est?.numeroProcesso}</TableCell>
                                                        <TableCell>{est?.directorTecnico?.dadosPessoais?.nome}</TableCell>
                                                        <TableCell><StatusEstabelecimento status={est?.status} /></TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Card>
                            )}
                        </>
                    )}

                    {/* Estado inicial — nenhuma pesquisa feita ainda */}
                    {pesquisa === null && !openLoad && !messageError && (
                        <Box textAlign="center" py={4}>
                            <SearchIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                            <Typography variant="body2" color="text.secondary" mt={1}>
                                Comece a digitar para pesquisar estabelecimentos.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
            </BootstrapDialog>
        </div>
    );
}


